import { useEffect, useRef, useState } from 'react'
import { updateParty } from '../../utils/partyGame'
import { DCDirectorBanner, DCHostDirectorLine } from './PartyDCIdentity'
import { IconDice, IconDirectorChair } from '../icons'

/**
 * Director's Cut — Phase 1: Director Selection + Director Reveal.
 *
 * role='host'     : tappable grid of player cards + Random card.
 * role='director' : same grid but read-only.
 * role='player'   : read-only "room is deciding" view.
 *
 * Status flow:
 *   director_selection → host taps → directorUid written → status: 'director_reveal'
 *   director_reveal    → 3-second dramatic reveal, then auto-advance to 'casting'
 */
export default function PartyDCDirectorSelect({ party, currentUserId, role }) {
  const players = party?.players ?? {}
  const playerEntries = Object.entries(players)
  const isHost   = role === 'host'
  const isRevealPhase = party?.status === 'director_reveal'
  const directorUid   = party?.directorUid
  const directorPlayer = directorUid ? players[directorUid] : null

  // Host: auto-advance from director_reveal → casting after 3 seconds
  const advancedRef = useRef(false)
  useEffect(() => {
    if (!isHost || !isRevealPhase || advancedRef.current) return
    if (!party?.id) return
    advancedRef.current = true
    const tid = setTimeout(() => {
      // Initialize agent points (everyone except the director gets a 0)
      const agentPoints = {}
      for (const uid of Object.keys(players)) {
        if (uid !== directorUid) agentPoints[uid] = 0
      }
      updateParty(party.id, {
        status: 'casting',
        dcStep: 'collect',
        dcNominations: {},
        castingNominations: {},
        agentPoints,
        dcChain: [],
        dcRoundHistory: [],
        dcStepStartedAt: new Date(),
      }).catch(() => {})
    }, 3000)
    return () => clearTimeout(tid)
  }, [isHost, isRevealPhase, party?.id, directorUid]) // eslint-disable-line

  // Once the reveal entrance animation has settled, swap the static tagline for a
  // live "getting ready" pulse. The reveal→casting transition has no data load —
  // it's a deliberate ~3s reveal beat plus host/player propagation lag — so this
  // makes the wait read as active motion instead of a frozen screen.
  const [revealSettled, setRevealSettled] = useState(false)
  useEffect(() => {
    if (!isRevealPhase) { setRevealSettled(false); return }
    const t = setTimeout(() => setRevealSettled(true), 1200)
    return () => clearTimeout(t)
  }, [isRevealPhase])

  // ── REVEAL PHASE ────────────────────────────────────────────────────────
  if (isRevealPhase && directorPlayer) {
    const isMe = directorUid === currentUserId
    const initial = (directorPlayer.username || '?').charAt(0).toUpperCase()
    return (
      <div className="dc-director-reveal">
        <div className="dc-director-reveal__avatar dc-director-reveal__avatar--pop">
          {directorPlayer.avatar
            ? <img src={directorPlayer.avatar} alt="" />
            : <span className="party-avatar__initial">{initial}</span>}
        </div>
        <div className="dc-director-reveal__chair" aria-hidden="true">
          <IconDirectorChair size={28} />
        </div>
        <h1 className="dc-director-reveal__title">
          {isMe ? "You're the Director!" : `${directorPlayer.username || 'Guest'} is your Director!`}
        </h1>
        {revealSettled ? (
          <p className="dc-director-reveal__loading" role="status" aria-live="polite">
            Getting the casting session ready…
          </p>
        ) : (
          <p className="dc-director-reveal__sub">
            {isMe ? 'You judge every decision. Make it count.' : 'They judge every decision. Make it count.'}
          </p>
        )}
      </div>
    )
  }

  // ── SELECTION PHASE ─────────────────────────────────────────────────────
  const handlePick = async (uid) => {
    if (!isHost || !party?.id) return
    await updateParty(party.id, {
      directorUid: uid,
      status: 'director_reveal',
      dcStepStartedAt: new Date(),
    }).catch(() => {})
  }

  const handleRandom = () => {
    if (!isHost || !party?.id || playerEntries.length === 0) return
    const uids = playerEntries.map(([uid]) => uid)
    handlePick(uids[Math.floor(Math.random() * uids.length)])
  }

  const isMeDirector = directorUid && currentUserId === directorUid
  return (
    <div className="dc-director-select">
      {isMeDirector && <DCDirectorBanner />}
      <h2 className="dc-director-select__title">
        {isHost ? "Who's calling the shots?" : 'The room is deciding who\'s in charge...'}
      </h2>
      {isHost && <DCHostDirectorLine party={party} />}
      {isHost && (
        <p className="dc-director-select__sub">
          Tap a player — or hit Random — to assign the Director.
        </p>
      )}
      <div className="dc-director-select__grid">
        {playerEntries.map(([uid, p]) => {
          const initial = (p.username || '?').charAt(0).toUpperCase()
          return (
            <button
              type="button"
              key={uid}
              className="dc-director-select__card"
              onClick={isHost ? () => handlePick(uid) : undefined}
              disabled={!isHost}
            >
              <div className="dc-director-select__card-avatar">
                {p.avatar
                  ? <img src={p.avatar} alt={p.username} />
                  : <span className="party-avatar__initial">{initial}</span>}
              </div>
              <span className="dc-director-select__card-name">{p.username || 'Guest'}</span>
            </button>
          )
        })}
        {/* Random card — always shown */}
        <button
          type="button"
          className="dc-director-select__card dc-director-select__card--random"
          onClick={isHost ? handleRandom : undefined}
          disabled={!isHost}
        >
          <div className="dc-director-select__card-avatar dc-director-select__card-avatar--random">
            <IconDice size={36} aria-hidden="true" />
          </div>
          <span className="dc-director-select__card-name">Random</span>
        </button>
      </div>
    </div>
  )
}
